import { PropertyType } from '@/backend/types'
import { LDEvents, useABMetrics } from '@/modules/App/useABMetrics'
import { usePropertyStore } from '@/modules/Project/usePropertyStore'
import { isCommandOrControlKey } from '@/shared/utils/event'
import { usePopoverStore } from '@/sharedComposables/popoverStore'
import { createSharedComposable } from '@vueuse/core'
import { computed, ref, useId, type ComponentPublicInstance } from 'vue'
import { usePropertyDialogIntegration } from './usePropertyDialogIntegration'

/**
 * This composable provides logic, state and event handlers for the popover menu
 * used to configure properties.
 */
export const usePropertyDialog = createSharedComposable(() => {
  const propertyStore = usePropertyStore()

  const {
    deleteProperty,
    deletePropertyConfirmationOpen,
    deselectProperty,
    hideProperty,
    reprocessColumn,
    saveEditedProperty,
    isPropertyBusy,
  } = usePropertyDialogIntegration()

  const closeMenu = () => deselectProperty()

  const isExpanded = ref(false)
  const anchorRefsById = ref<Record<string, HTMLElement | ComponentPublicInstance | null>>({})
  const anchorRef = computed(() =>
    propertyStore.editedProperty?.id
      ? (anchorRefsById.value[propertyStore.editedProperty.id] ?? null)
      : null,
  )

  const editMode = ref<'generated' | 'manual'>()

  const selectForEditing = async (propertyId: string, mode: 'generated' | 'manual') => {
    // we're already editing a property. it could be the same or a different one
    // we need to save it if it's not a generated property
    // generated properties are unsaved and get dismissed when clicking away from them
    if (propertyStore.editedProperty && editMode.value !== 'generated') {
      const savedOk = await saveEditedProperty()
      if (!savedOk) return
    }

    // if we're selecting an entity from the header, that entity is guaranteed to not be generated
    editMode.value = mode

    // if we're selecting a different property, we need to start editing it
    if (propertyStore.editedProperty?.id !== propertyId) {
      propertyStore.startEditingProperty(propertyId)
    } else {
      // if we're selecting the same property, we need to deselect it
      deselectProperty()
    }
  }

  const { trackElapsedTimeEvent } = useABMetrics()

  const updatePropertyAndCloseMenu = async () => {
    const savedOk = await saveEditedProperty()
    if (!savedOk) return

    trackElapsedTimeEvent(LDEvents.TIME_SPENT_ON_EDITING)

    editMode.value = undefined
    isExpanded.value = false
    deselectProperty()
  }

  const onReprocessColumn = async () => {
    if (propertyStore.isDirty) {
      const result = await saveEditedProperty()
      // leave the menu open if the save failed
      if (!result) return
    }

    await reprocessColumn()
    deselectProperty()
  }

  const openDeleteDialog = () => {
    deletePropertyConfirmationOpen.value = true
  }

  const canCloseCollectionMenu = computed<boolean>(() => {
    return (
      PropertyType.collection !== propertyStore.editedProperty?.type ||
      (PropertyType.collection === propertyStore.editedProperty?.type &&
        propertyStore.editedProperty?.tool === 'manual') ||
      propertyStore.visibleProperties.length > 0
    )
  })

  const collectionError = computed(() => !canCloseCollectionMenu.value)

  /** The user must have selected a related agent in order to save a reference property. */
  const canSaveReferenceProperty = computed(() => {
    if (propertyStore.editedProperty?.type !== 'reference') {
      return true
    }

    return !!propertyStore.editedProperty.config.projectId
  })

  const popoverStore = usePopoverStore()
  const relatedProjectMenuItemId = useId()
  const closeRelatedProjectValidationPopover = () => {
    if (popoverStore.popover?.targetSelector === `#${relatedProjectMenuItemId}`) {
      popoverStore.setPopover(null)
    }
  }

  const validateCanSave = (): boolean => {
    if (!canCloseCollectionMenu.value) {
      return false
    }

    if (!canSaveReferenceProperty.value) {
      popoverStore.setPopover({
        text: 'Please select a source',
        placement: { allowedPlacements: ['left', 'right'] },
        targetSelector: `#${relatedProjectMenuItemId}`,
      })
      return false
    }

    return true
  }

  const onPromptKeydown = async (e: KeyboardEvent) => {
    if (!(e.key === 'Enter' && isCommandOrControlKey(e))) return

    e.stopPropagation()

    const canSave = validateCanSave()
    if (!canSave) {
      return
    }

    const savedOk = await saveEditedProperty()
    if (!savedOk) return

    deselectProperty()
  }

  const handleClickOutside = async (e: Event) => {
    if (isExpanded.value) {
      return
    }

    // header clicks are ignored by click outside
    // instead, the call to selectForEditing handles it internally
    const target = e.target as HTMLElement
    const isClickOnHeader =
      target instanceof HTMLElement &&
      Object.values(anchorRefsById.value).some((ref) => {
        if (ref instanceof HTMLElement) {
          return ref === target || ref.contains(target)
        }
        if (ref) {
          return ref.$el === target || ref.$el.contains(target)
        }
        return false
      })
    if (isClickOnHeader) {
      return
    }

    if (!validateCanSave()) return

    // A previously persisted and dirty property should be saved on dismiss, unless it's a generated property
    // generated properties render an explicit save/cancel button in the dialog, so click-outside just dismisses them
    const result =
      propertyStore.originalProperty && propertyStore.isDirty && editMode.value !== 'generated'
        ? await saveEditedProperty()
        : null

    /**
     * This `if` condition was added to prevent flakey tests, but it could also
     * be a real problem in the app on particularly slow connections.
     *
     * The problem happens when:
     * 1. User clicks outside, we make API request to save changes
     * 2. Some other thing happens in the app to deselect the property
     * 3. User selects another property
     * 4. API request from (1) resolves and we deselect the property that
     *    the user just selected in (3).
     */
    if (!result || (result.ok && result.data.id === propertyStore.editedProperty?.id)) {
      deselectProperty()
    }
  }

  return {
    anchorRef,
    anchorRefsById,
    canCloseCollectionMenu,
    canSaveReferenceProperty,
    closeMenu,
    closeRelatedProjectValidationPopover,
    collectionError,
    deleteProperty,
    deletePropertyConfirmationOpen,
    editMode,
    handleClickOutside,
    hideProperty,
    isExpanded,
    isPropertyBusy,
    onPromptKeydown,
    onReprocessColumn,
    openDeleteDialog,
    relatedProjectMenuItemId,
    saveEditedProperty,
    selectForEditing,
    updatePropertyAndCloseMenu,
    validateCanSave,
  }
})
